import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import { useContext } from 'react';
import { Paths } from '../../pages';

import LoadingScreen from '../LoadingScreen';
import { DrinkUpServiceApiContext } from '../../DrinkUpServiceApiContext';

interface CreateUserFormProps {
    onSuccess: () => void;
}

function CreateUserForm({ onSuccess }: CreateUserFormProps): JSX.Element {
    // Form Data
    const { drinkUpService } = useContext(DrinkUpServiceApiContext);
    const [ableToSubmit, setAbleToSubmit] = useState<boolean>(true);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [drinkUpPassword, setDrinkUpPassword] = useState<string>('');
    const [isCreatingUser, setCreatingUser] = useState<boolean>(false);
    const [isCreationSuccessful, setIsCreationSuccessful] = useState<boolean>(false);
    const [isCreationFailure, setIsCreationFailure] = useState<boolean>(false);
    const [failureReason, setFailureReason] = useState<string | null>(null);

    const validateForm = (): boolean => {

        if (!!firstName && !!lastName && !!emailAddress && !!drinkUpPassword) {
            
            if(firstName === "") {
                console.log("aa")
                setFailureReason("first name cannot be empty");
                setAbleToSubmit(false);
                return false;
            }

            if(lastName === "") {
                setAbleToSubmit(false);
                setFailureReason("last name cannot be empty");
                return false;
            }

            if(emailAddress === "") {
                setAbleToSubmit(false);
                setFailureReason("email address cannot be empty");
                return false;
            }

            if(drinkUpPassword.length < 8) {
                setAbleToSubmit(false);
                setFailureReason("Rumbustible Password must be 8 characters or longer");
                return false;
            }
            
            setFailureReason(null);
            setAbleToSubmit(true);
            return true;
        } else {
            setAbleToSubmit(false);
            setFailureReason("all fields need to be filled out");
            return false;
        }
    };

    const createUser = async ({ 
        emailAddress, firstName, lastName, drinkUpPassword
    }: {
        emailAddress: string;
        firstName: string;
        lastName: string;
        drinkUpPassword: string;
    }): Promise<boolean> => {
        try {
            const res = await drinkUpService.post(Paths.api.create, {
                // TODO Update User Details mapper to include email Address
                email: emailAddress,
                firstName: firstName,
                lastName: lastName,
                password: drinkUpPassword
            });
            console.log("create response:", res);

            const success = res.status === 201;
            if (success) {
                return success;
            } else {
                return false;
            }
        } catch (e) {
            console.error('failed to create user', e)
            return false;
        }
    };

    const onFormControlChngWrapper = (changefn: (value: string) => void) => (el: React.ChangeEvent<HTMLInputElement>) => {
        el.preventDefault();

        changefn(el.target.value);
        validateForm();
    }

    const onSubmit = async (el: React.FormEvent<HTMLFormElement>): Promise<void> => {
        el.preventDefault()

        console.log("email:" + emailAddress);

        if(validateForm() === false) {
            return;
        }

        if (!ableToSubmit) return;

        // Submit Form via POST
        const userToCreate = {
            firstName, lastName, emailAddress, drinkUpPassword
        };
        // set loading
        setCreatingUser(true);

        try {
            const resStatus = await createUser(userToCreate);

            console.log("createUser result: " + resStatus);
            setIsCreationSuccessful(resStatus);
            if(!resStatus) {
                setIsCreationFailure(true);
            }
        } catch(e) {
            setIsCreationFailure(true);
        } finally {
            setCreatingUser(false);
        }
    };

    if(isCreatingUser) {
        return (<>
            <Stack className='text-center'>
                <LoadingScreen />
            </Stack>
        </>);
    }

    if(isCreationSuccessful) {
        return (<>
            <Stack gap={3} className='text-center'>                
                <h1>Welcome to the Rumbustion Society!</h1>
                <p>Before you go to login, please read the following:</p>
                <p>After you've ordered a rum chapter or rum, please wait until after the rum is actually served to you to request it here in Rumbustible.  Then please put in your request right away (the same night as your order) so management can verify your purchase against your tab. </p>
                <p>NOTE: If someone else is purchasing a rum on your behalf, please make sure to check the "Gifted By" box in the request screen and put in the name of the generous individual so the staff knows which tab to review when approving it.</p>
                <p>After you've finished all 20 chapters and have taken the quiz to become a Disciple of the Cove you will gain access to the full rum list. However, if you do order any rums from our printed rum list before you finish your chapters, make sure to save your receipts so you can request a sign-off for those rums once you become a Disciple.</p>
                <p>
                    Mobile apps also exist to track your rum journey on 
                    &nbsp;<a href="https://testflight.apple.com/join/AX2HdrTH">iOS</a>
                    &nbsp;and&nbsp;<a href="https://play.google.com/store/apps/details?id=com.shea.ryan.rumbustion">Android</a>
                </p>
                <p>Enjoy your journey through the wonderful world of rum!</p>
                <Button variant="primary" onClick={onSuccess}>Go to Login</Button>
            </Stack>
        </>);
    }

    if(isCreationFailure) {
        return (
            <p>User creation failed, please contact an administrator for support.</p>
        );
    }

    return (
        <Form style={{ 'minWidth': '20rem' }} onSubmit={onSubmit}>
            <Form.Group className="mb-3" controlId="formEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control type="email" placeholder="Enter email" onChange={onFormControlChngWrapper(setEmailAddress)} />
                <Form.Text className="text-muted">
                    This email will represent your login
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formFirstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control type="text" placeholder="First Name" onChange={onFormControlChngWrapper(setFirstName)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control type="text" placeholder="Last Name" onChange={onFormControlChngWrapper(setLastName)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formDrinkUpPw">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" placeholder="Password" onChange={onFormControlChngWrapper(setDrinkUpPassword)} />
            </Form.Group>
            {
                !!failureReason
                    ? <><p style={{ 'color': 'red' }}>{failureReason}</p></> : ""
            }
            <div>
                <Button variant="primary" className="ml-auto"
                    type="submit">
                    Create Account
                </Button>
            </div>
        </Form >
    );
}

export { CreateUserForm };
